import { Button } from "@/components/ui/button";
import { 
  ArrowRight, 
  Settings, 
  Headphones, 
  ShieldCheck, 
  Lightbulb,
  ArrowLeft
} from "lucide-react";
import { Link } from "react-router-dom";
import qcardLogo from "@/assets/qcard-logo-official.png";


const enterpriseFeatures = [
  {
    icon: Settings,
    title: "Integrates With Your Enterprise",
    color: "bg-blue-100 text-blue-600",
    items: [
      "Integrations with enterprise HRMS and CRM systems",
      "Unified and controlled platform to manage employee network and access",
      "APIs",
      "The QCard team can create custom URLs tailored to your company's domain"
    ]
  },
  {
    icon: Headphones,
    title: "Enterprise-Level Customer Support",
    color: "bg-orange-100 text-orange-500",
    items: [
      "Seamless and quick user onboarding and system implementation",
      "Employee training on how to use the platform",
      "24/7 Helpdesk",
      "360-Degree Customer Success Team",
      "Personal Concierge",
      "Rollout support"
    ]
  },
  {
    icon: ShieldCheck,
    title: "Secure",
    color: "bg-pink-100 text-pink-500",
    items: [
      "Single Sign-On",
      "Enterprise-grade security",
      "On Prem/Cloud Solutions"
    ]
  },
  {
    icon: Lightbulb,
    title: "Custom Solutions",
    color: "bg-green-100 text-green-500",
    items: [
      "Collaborate with the QCard team to tailor solutions for the specific needs of your enterprise",
      "In-house experts for expanded customer experience solutions"
    ]
  }
];

const EnterprisePlan = () => {
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="border-b border-border/50 bg-background/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/homepage-enterprise" className="flex items-center gap-2">
            <img src={qcardLogo} alt="QCard" className="h-8 w-auto" />
          </Link>
          <Link 
            to="/homepage-enterprise" 
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              Designed for{" "}
              <span className="text-enterprise-purple">Enterprises</span>,
              <br />
              from the Ground Up
            </h1>
            <p className="text-xl text-muted-foreground">
              QCard provides the cutting edge features, infrastructure, security and support that enterprises need.
            </p>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 md:py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {enterpriseFeatures.map((feature, index) => (
              <div 
                key={index}
                className="bg-card border border-border rounded-2xl p-8 hover:shadow-lg transition-all duration-300"
              >
                {/* Icon */}
                <div className={`w-16 h-16 rounded-2xl ${feature.color} flex items-center justify-center mb-6`}>
                  <feature.icon className="w-8 h-8" />
                </div>
                
                {/* Title */}
                <h3 className="text-xl font-bold mb-4">{feature.title}</h3>
                
                {/* Items */}
                <ul className="space-y-3">
                  {feature.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-start gap-3 text-muted-foreground">
                      <span className="w-1.5 h-1.5 rounded-full bg-enterprise-purple mt-2 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              Ready to equip your team with QCard?
            </h2>
            <p className="text-muted-foreground mb-8">
              Get in touch with our enterprise team to discuss your organization's needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                className="bg-enterprise-purple hover:bg-enterprise-purple-dark text-white text-lg px-8 py-6 font-semibold"
                asChild
              >
                <a href="mailto:info@qcardenterprise.com">
                  Contact Sales
                  <ArrowRight className="ml-2 h-5 w-5" />
                </a>
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="text-lg px-8 py-6"
                asChild
              >
                <Link to="/homepage-enterprise" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                  Learn More About QCard
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-slate-950 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-5xl font-bold mb-6">
              Stop Being{" "}
              <span className="text-white/40">Forgotten.</span>
              <br />
              Start Being{" "}
              <span className="text-primary">Saved.</span>
            </h2>
            <p className="text-white/50 text-lg mb-8 max-w-xl mx-auto">
              Join the professionals who refuse to let great connections slip away. Your QCard is free, forever.
            </p>
            <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground text-lg px-12 py-7 font-semibold shadow-lg shadow-primary/30 hover:shadow-xl hover:shadow-primary/40 transition-all" asChild>
              <Link to="/homepage-enterprise" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                Get Your Free QCard — And Never Be Forgotten Again
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <p className="mt-4 text-sm text-white/30">
              No credit card required. Set up in 60 seconds.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EnterprisePlan;
